import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Upload, Video, Image, Loader2, ChevronLeft, Play, ShoppingCart } from 'lucide-react';
import api from '@/utils/api';

const VendreVideosPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [boutiqueVideos, setBoutiqueVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(false);

  const [formData, setFormData] = useState({
    titre: '',
    categorie: '',
    prix: '',
    date_sortie: '',
    description: '',
    video_file: null,
    miniature_file: null,
    miniature_type: 'image' // 'image' ou 'video'
  });

  const [videoPreview, setVideoPreview] = useState(null);
  const [miniaturePreview, setMiniaturePreview] = useState(null);

  const categories = [
    'Clip Musical',
    'Comédie',
    'Série',
    'Film',
    'Autre'
  ];

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'video/mp4') {
        toast.error('Veuillez sélectionner un fichier MP4');
        return;
      }
      setFormData({ ...formData, video_file: file });
      setVideoPreview(URL.createObjectURL(file));
    }
  };

  const handleMiniatureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const isImage = ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type);
      const isVideo = file.type === 'video/mp4';
      
      if (!isImage && !isVideo) {
        toast.error('Seuls les fichiers JPG/PNG (image) ou MP4 (vidéo teaser) sont acceptés');
        return;
      }
      
      // Validation taille
      if (isImage && file.size > 10 * 1024 * 1024) {
        toast.error('L\'image ne doit pas dépasser 10MB');
        return;
      }
      
      if (isVideo && file.size > 50 * 1024 * 1024) {
        toast.error('La vidéo teaser ne doit pas dépasser 50MB');
        return;
      }
      
      const miniature_type = isVideo ? 'video' : 'image';
      setFormData({ ...formData, miniature_file: file, miniature_type });
      setMiniaturePreview(URL.createObjectURL(file));
    }
  };

  // Charger les vidéos de la boutique
  useEffect(() => {
    const fetchBoutiqueVideos = async () => {
      setLoadingVideos(true);
      try {
        const response = await api.get('/boutique');
        setBoutiqueVideos(response.data || []);
      } catch (error) {
        console.error('Erreur lors du chargement des vidéos:', error);
      } finally {
        setLoadingVideos(false);
      }
    };

    fetchBoutiqueVideos();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      toast.error('Veuillez vous connecter pour vendre vos vidéos');
      navigate('/auth');
      return;
    }

    // Validations
    if (!formData.titre) {
      toast.error('Veuillez entrer le titre de la vidéo');
      return;
    }
    if (!formData.categorie) {
      toast.error('Veuillez choisir une catégorie');
      return;
    }
    if (!formData.prix) {
      toast.error('Veuillez fixer le prix de votre vidéo');
      return;
    }
    if (parseFloat(formData.prix) < 0) {
      toast.error('Le prix ne peut pas être négatif');
      return;
    }
    if (!formData.date_sortie) {
      toast.error('Veuillez indiquer la date de sortie de la vidéo');
      return;
    }
    if (!formData.video_file) {
      toast.error('Veuillez télécharger votre vidéo MP4');
      return;
    }
    if (!formData.description) {
      toast.error('Veuillez entrer une description');
      return;
    }
    if (!formData.miniature_file) {
      toast.error('Veuillez télécharger une miniature (image de couverture)');
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('titre', formData.titre);
      formDataToSend.append('categorie', formData.categorie);
      formDataToSend.append('prix', formData.prix);
      formDataToSend.append('date_sortie', formData.date_sortie);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('video', formData.video_file);
      formDataToSend.append('miniature', formData.miniature_file);
      formDataToSend.append('miniature_type', formData.miniature_type); // Type: 'image' ou 'video'

      const response = await api.post('/videos/vendre', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        toast.success('Vidéo envoyée avec succès ! Notre équipe va la valider.');
        // Reset form
        setFormData({
          titre: '',
          categorie: '',
          prix: '',
          date_sortie: '',
          description: '',
          video_file: null,
          miniature_file: null
        });
        setVideoPreview(null);
        setMiniaturePreview(null);
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi de la vidéo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 via-purple-50 to-orange-50">
      {/* Header avec dégradé luxe */}
      <div className="relative z-10 border-b-4 border-purple-300 bg-gradient-to-r from-purple-100 via-pink-100 to-orange-100 shadow-lg sticky top-0 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-purple-700 hover:text-purple-900 transition-colors font-bold text-lg"
          >
            <ChevronLeft size={24} />
            <span>Retour</span>
          </button>
          <h1 className="text-2xl md:text-3xl font-black bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 bg-clip-text text-transparent" style={{ fontFamily: 'Outfit' }}>
            🎬 VENDRE MES VIDÉOS / CLIPS
          </h1>
          <div className="w-24"></div>
        </div>
      </div>

      <main className="relative z-10 pt-12 pb-20 max-w-4xl mx-auto px-4">
        {/* Titre avec dégradé éclatant */}
        <div className="text-center mb-8">
          <h2 className="text-5xl md:text-6xl font-black mb-4 bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 bg-clip-text text-transparent drop-shadow-lg" 
              style={{ textShadow: '0 0 60px rgba(168,85,247,0.3)' }}>
            📹 Vendez une vidéo sur AGRP Box
          </h2>
          <div className="h-2 w-64 mx-auto bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 rounded-full shadow-lg"></div>
        </div>

        <Card className="bg-white/95 backdrop-blur-sm border-4 border-purple-300 shadow-2xl shadow-purple-500/30 relative">
          <CardHeader className="bg-gradient-to-r from-purple-50 via-pink-50 to-orange-50 border-b-4 border-orange-300">
            <CardTitle className="text-purple-900 text-3xl font-black flex items-center gap-3">
              <span className="text-4xl">🎥</span>
              Publiez Votre Vidéo
            </CardTitle>
            <CardDescription className="text-purple-700 text-lg font-semibold">
              Remplissez le formulaire ci-dessous pour mettre en vente votre création
            </CardDescription>
          </CardHeader>

          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* 1. Titre de la vidéo */}
              <div className="p-6 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl border-2 border-purple-300">
                <Label htmlFor="titre" className="text-purple-900 font-bold text-lg flex items-center gap-2">
                  <span className="text-2xl">1️⃣</span> Titre de la vidéo *
                </Label>
                <Input
                  id="titre"
                  type="text"
                  placeholder="Titre de votre vidéo"
                  value={formData.titre}
                  onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                  className="bg-white border-2 border-purple-300 text-purple-900 mt-3 text-lg font-semibold focus:border-purple-500 focus:ring-purple-500"
                />
              </div>

              {/* 2. Type / Catégorie */}
              <div className="p-6 bg-gradient-to-r from-pink-50 to-pink-100 rounded-xl border-2 border-pink-300">
                <Label htmlFor="categorie" className="text-pink-900 font-bold text-lg flex items-center gap-2">
                  <span className="text-2xl">2️⃣</span> Type / Catégorie de vidéo *
                </Label>
                <select
                  id="categorie"
                  value={formData.categorie}
                  onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
                  className="w-full h-12 px-4 py-2 mt-3 bg-white border-2 border-pink-300 text-pink-900 font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 cursor-pointer text-lg"
                >
                  <option value="" disabled>Choisissez une catégorie</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat} className="bg-white text-pink-900">
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              {/* 3. Prix de vente */}
              <div className="p-6 bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl border-2 border-orange-300">
                <Label htmlFor="prix" className="text-orange-900 font-bold text-lg flex items-center gap-2">
                  <span className="text-2xl">3️⃣</span> Prix de vente (en dollars USD) *
                </Label>
                <div className="relative mt-3">
                  <span className="absolute left-4 top-4 text-orange-600 font-black text-xl">$</span>
                  <Input
                    id="prix"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Ex: 5.00"
                    value={formData.prix}
                    onChange={(e) => setFormData({ ...formData, prix: e.target.value })}
                    className="bg-white border-2 border-orange-300 text-orange-900 pl-10 text-lg font-bold focus:border-orange-500 focus:ring-orange-500"
                  />
                </div>
                <p className="text-sm text-orange-700 mt-2 font-semibold flex items-center gap-2">
                  💰 Fixez le prix auquel vous souhaitez vendre votre vidéo
                </p>
              </div>

              {/* 4. Date de sortie */}
              <div className="p-6 bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-xl border-2 border-yellow-400">
                <Label htmlFor="date_sortie" className="text-yellow-900 font-bold text-lg flex items-center gap-2">
                  <span className="text-2xl">4️⃣</span> Date de sortie de la vidéo *
                </Label>
                <Input
                  id="date_sortie"
                  type="date"
                  value={formData.date_sortie}
                  onChange={(e) => setFormData({ ...formData, date_sortie: e.target.value })}
                  className="bg-white border-2 border-yellow-400 text-yellow-900 mt-3 text-lg font-bold focus:border-yellow-500 focus:ring-yellow-500"
                  required
                />
                <p className="text-sm text-yellow-700 mt-2 font-semibold flex items-center gap-2">
                  📅 Indiquez quand cette vidéo est sortie (jour/mois/année)
                </p>
              </div>

              {/* 5. Upload Vidéo */}
              <div className="p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border-2 border-purple-300">
                <Label htmlFor="video" className="text-purple-900 font-bold text-lg flex items-center gap-2">
                  <span className="text-2xl">5️⃣</span> Mettre votre vidéo * (MP4)
                </Label>
                <div className="mt-3">
                  <label 
                    htmlFor="video-upload" 
                    className="flex items-center justify-center gap-4 bg-white border-4 border-dashed border-purple-400 hover:border-purple-600 rounded-2xl p-10 cursor-pointer transition-all hover:shadow-lg hover:shadow-purple-300"
                  >
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center">
                      <Upload size={36} className="text-white" />
                    </div>
                    <div className="text-center">
                      <p className="text-purple-900 font-bold text-lg">
                        {formData.video_file ? `✅ ${formData.video_file.name}` : '📹 Cliquez pour télécharger votre vidéo MP4'}
                      </p>
                      <p className="text-purple-600 text-sm font-semibold">Format accepté : MP4 (jusqu'à 500MB)</p>
                    </div>
                  </label>
                  <input
                    id="video-upload"
                    type="file"
                    accept="video/mp4"
                    onChange={handleVideoChange}
                    className="hidden"
                  />
                  {videoPreview && (
                    <video 
                      src={videoPreview} 
                      controls 
                      className="mt-4 w-full max-h-64 rounded-xl border-4 border-purple-300 shadow-lg"
                    />
                  )}
                </div>
              </div>

              {/* 6. Description */}
              <div className="p-6 bg-gradient-to-r from-pink-50 to-orange-50 rounded-xl border-2 border-pink-300">
                <Label htmlFor="description" className="text-pink-900 font-bold text-lg flex items-center gap-2">
                  <span className="text-2xl">6️⃣</span> Description de la vidéo *
                </Label>
                <Textarea
                  id="description"
                  placeholder="Décrivez votre vidéo, l'histoire, les artistes..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={5}
                  className="bg-white border-2 border-pink-300 text-pink-900 mt-3 text-lg font-medium focus:border-pink-500 focus:ring-pink-500"
                />
              </div>

              {/* 7. Miniature (Image OU Vidéo teaser) */}
              <div className="p-6 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl border-2 border-orange-300">
                <Label htmlFor="miniature" className="text-orange-900 font-bold text-lg flex items-center gap-2 mb-2">
                  <span className="text-2xl">7️⃣</span> Couverture de la vidéo * (Photo OU Vidéo Teaser)
                </Label>
                <p className="text-orange-700 text-sm font-semibold mb-3">
                  🎬 Choisissez : <span className="text-orange-900">Photo JPG/PNG</span> OU <span className="text-orange-900">Vidéo teaser 15-30s (MP4, max 50MB)</span>
                </p>
                <div className="mt-3">
                  <label 
                    htmlFor="miniature-upload" 
                    className="flex items-center justify-center gap-4 bg-white border-4 border-dashed border-orange-400 hover:border-orange-600 rounded-2xl p-10 cursor-pointer transition-all hover:shadow-lg hover:shadow-orange-300"
                  >
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center">
                      <Image size={36} className="text-white" />
                    </div>
                    <div className="text-center">
                      <p className="text-orange-900 font-bold text-lg">
                        {formData.miniature_file ? (
                          formData.miniature_type === 'video' 
                            ? `✅ Vidéo teaser: ${formData.miniature_file.name}` 
                            : `✅ Photo: ${formData.miniature_file.name}`
                        ) : '🖼️ Photo (JPG/PNG) OU 🎬 Vidéo teaser (MP4)'}
                      </p>
                      <p className="text-orange-600 text-sm font-semibold mt-1">
                        Image max 10MB • Vidéo teaser max 50MB
                      </p>
                    </div>
                  </label>
                  <input
                    id="miniature-upload"
                    type="file"
                    accept="image/jpeg,image/png,image/jpg,video/mp4"
                    onChange={handleMiniatureChange}
                    className="hidden"
                  />
                  {miniaturePreview && (
                    <div className="mt-4">
                      {formData.miniature_type === 'video' ? (
                        <video 
                          src={miniaturePreview} 
                          controls
                          autoPlay
                          muted
                          loop
                          className="w-full max-h-64 object-contain rounded-xl border-4 border-orange-300 shadow-lg bg-black"
                        />
                      ) : (
                        <img 
                          src={miniaturePreview} 
                          alt="Miniature preview" 
                          className="mt-4 w-full max-h-64 object-cover rounded-xl border-4 border-orange-300 shadow-lg"
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Bouton Submit avec dégradé luxe */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-8 text-2xl font-black text-white rounded-2xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed shadow-2xl hover:shadow-purple-500/50 relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #9333ea 0%, #ec4899 50%, #f59e0b 100%)',
                }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-3">
                    <Loader2 className="animate-spin" size={32} />
                    ⏳ Envoi en cours...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-3">
                    <Video size={32} />
                    🚀 PUBLIER MA VIDÉO MAINTENANT
                  </span>
                )}
              </button>
            </form>
          </CardContent>
        </Card>

        {/* Section Galerie des Vidéos Publiées */}
        <div className="mt-16 bg-white/95 backdrop-blur-sm rounded-3xl p-8 border-4 border-purple-300 shadow-2xl">
          <div className="text-center mb-8">
            <h2 className="text-4xl md:text-5xl font-black mb-4 bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 bg-clip-text text-transparent">
              🎬 Vidéos Déjà Publiées
            </h2>
            <div className="h-1 w-48 mx-auto bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 rounded-full"></div>
          </div>

          {loadingVideos ? (
            <div className="text-center py-12">
              <Loader2 className="animate-spin mx-auto mb-4 text-purple-500" size={48} />
              <p className="text-purple-700 font-semibold">Chargement des vidéos...</p>
            </div>
          ) : boutiqueVideos.length === 0 ? (
            <Card className="bg-white/95 border-4 border-purple-300 p-12 text-center">
              <Video size={64} className="mx-auto mb-4 text-purple-500" />
              <p className="text-purple-900 font-bold text-xl">Aucune vidéo publiée pour le moment</p>
              <p className="text-purple-700 mt-2">Soyez le premier à publier !</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {boutiqueVideos.map((video) => (
                <Card 
                  key={video.id} 
                  className="bg-white/95 border-4 border-purple-300 hover:border-pink-400 transition-all hover:scale-105 hover:shadow-xl hover:shadow-purple-500/50 cursor-pointer overflow-hidden"
                  onClick={() => navigate(`/boutique`)}
                >
                  <div className="relative">
                    {video.cover_image_path ? (
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/storage/${video.cover_image_path}`}
                        alt={video.title}
                        className="w-full h-48 object-cover"
                      />
                    ) : (
                      <div className="w-full h-48 bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                        <Play size={64} className="text-white" />
                      </div>
                    )}
                    <div className="absolute top-3 right-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-1 rounded-full font-bold text-sm shadow-lg">
                      ${video.price_usd}
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-black text-purple-900 text-lg mb-2 line-clamp-1">
                      {video.title}
                    </h3>
                    <p className="text-pink-700 font-semibold text-sm mb-3 line-clamp-2">
                      {video.description}
                    </p>
                    {video.date_sortie && (
                      <div className="mb-2">
                        <span className="text-xs text-orange-600 font-bold bg-orange-100 px-2 py-1 rounded">
                          📅 Sortie : {new Date(video.date_sortie).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-xs text-purple-600 font-semibold">
                      <span>👤 {video.artist_name || video.user_name}</span>
                      <span className="flex items-center gap-1">
                        <ShoppingCart size={14} />
                        {video.purchases || 0}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default VendreVideosPage;
